// Lets a teacher build and maintain their own class sections and rosters
// without an admin doing it for them. Rosters are optional school-wide, so a
// school adopts them one teacher at a time — this panel is that on-ramp.
//
// Everything here goes through the same class-sections routes the admin
// panel uses; the backend enforces the teacher-scoped rules (own sections
// only, `name` is the only editable field, and a roster replace that would
// take students out of a colleague's section 409s until it's confirmed).
// None of that is re-implemented client-side: the 403/404/409 responses are
// surfaced, not predicted.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import CollapsibleSection from '../common/CollapsibleSection';
import { ApiError } from '../../api/http';
import {
  createClassSection,
  deleteClassSection,
  getRoster,
  listSchoolPeriods,
  putRoster,
  updateClassSection,
  type ClassSectionRosterConflictResponse,
} from '../../api/schoolConfigApi';
import { useUserNameResolver, userLabel } from '../../hooks/useUserNameResolver';
import { readFileAsText } from '../../utils/csvParsing';
import { showError, showSuccess } from '../../utils/notifications';
import { getContextualError, logError } from '../../utils/errorHandling';
import type { ClassSection } from '../../hooks/useClassSections';
import type { components } from '../../api/types.gen';
import adminStyles from '../admin/AdminPanel.module.css';
import styles from './MyClassSectionsPanel.module.css';

type SchoolPeriodResponse = components['schemas']['SchoolPeriodResponse'];
type ClassSectionRosterReassignment =
  components['schemas']['ClassSectionRosterReassignment'];

export interface MyClassSectionsPanelProps {
  /** Every section in the school; filtered to the caller's own below. */
  classSections: ClassSection[];
  /** Re-fetches the shared list after this panel mutates it. */
  refetchClassSections: () => Promise<void>;
  /**
   * True while the shared list is still loading. Without it an empty
   * `classSections` is indistinguishable from 'no classes', and
   * `availablePeriods` would offer periods the teacher already owns.
   */
  loading?: boolean;
}

/**
 * Splits pasted or uploaded text into email addresses. Deliberately looser
 * than parseCsv: a teacher pastes a column out of a spreadsheet, a
 * comma-separated line, or a one-per-line list, and all three should work.
 * Blank entries are dropped; a header row like "email" is dropped too, since
 * copying a column header along with the column is the common accident.
 */
export function parseEmailList(text: string): string[] {
  const tokens = text
    .split(/[\s,;]+/)
    .map((t) => t.trim().replace(/^["']|["']$/g, ''))
    .filter((t) => t.length > 0 && t.toLowerCase() !== 'email');
  // De-duplicate case-insensitively: the backend 422s on a duplicate, and a
  // teacher pasting the same student twice is a typo, not a request.
  const seen = new Set<string>();
  const out: string[] = [];
  tokens.forEach((t) => {
    const key = t.toLowerCase();
    if (!seen.has(key)) {
      seen.add(key);
      out.push(t);
    }
  });
  return out;
}

/** Reads the conflict list off a 409 without trusting the body's shape. */
function readConflicts(error: unknown): ClassSectionRosterReassignment[] | null {
  if (!(error instanceof ApiError) || error.status !== 409) return null;
  const body = error.body as ClassSectionRosterConflictResponse | null;
  if (!body || !Array.isArray(body.conflicts)) return null;
  return body.conflicts;
}

const MyClassSectionsPanel = ({
  classSections,
  refetchClassSections,
  loading = false,
}: MyClassSectionsPanelProps): React.ReactElement | null => {
  const { currentUser } = useAuth();
  const schoolId = currentUser?.schoolId;
  const teacherId = currentUser?.id;

  const [schoolPeriods, setSchoolPeriods] = useState<SchoolPeriodResponse[]>([]);
  const [newPeriodId, setNewPeriodId] = useState('');
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  const [openSectionId, setOpenSectionId] = useState<number | null>(null);
  const [rosterLoading, setRosterLoading] = useState(false);
  // getRoster failed for the open section, so `rosterStudentIds` is []
  // for want of data, not because the roster is empty. PUT /roster is a
  // full replace, so saving that would delete the real roster.
  const [rosterLoadFailed, setRosterLoadFailed] = useState(false);
  const [rosterStudentIds, setRosterStudentIds] = useState<number[]>([]);
  // Roster ids whose listUsers lookup THREW, as opposed to ids the server
  // simply did not return. Only the former is retryable: a soft-deleted
  // student is returned by GET /roster forever but filtered out of
  // GET /api/users?ids= (docs/FOLLOW_UPS.md, "Soft-deleting a user leaves
  // their ClassEnrollment rows"), so treating an omission as a failure
  // locks the editor permanently.
  const [lookupFailedIds, setLookupFailedIds] = useState<string[]>([]);
  // Roster ids whose listUsers lookup threw on a lookup issued AFTER a
  // successful save. Deliberately a SEPARATE atom from `lookupFailedIds`,
  // which drives the save gate: these two look identical (a thrown chunk)
  // but answer different questions. `lookupFailedIds` asks "could the box
  // the teacher is about to send be missing students it cannot name?";
  // this asks "are the labels in the summary above degraded?". After a
  // save the answer to the first is always no — the server echoed the ids
  // that the box itself produced — so feeding a post-save failure into the
  // gate is a false alarm that disables Save on a box that is exactly
  // right. One atom answering both questions is what iteration-3 got wrong.
  const [namesDegradedIds, setNamesDegradedIds] = useState<string[]>([]);
  const [emailsText, setEmailsText] = useState('');
  const [saving, setSaving] = useState(false);
  const [conflicts, setConflicts] = useState<
    ClassSectionRosterReassignment[] | null
  >(null);
  // The conflict-name lookup THREW, so the confirmation list is rendering
  // ids instead of people. Distinct from 'a conflicting student has no
  // account': only a thrown chunk lands in `failedIds`, so a soft-deleted
  // student still renders as 'Student #<id>' WITHOUT gating the confirm —
  // that would be an unrecoverable lock.
  const [conflictNamesFailed, setConflictNamesFailed] = useState(false);

  const [renamingId, setRenamingId] = useState<number | null>(null);
  const [renameValue, setRenameValue] = useState('');
  // The section id whose rename/delete is in flight. `creating`/`saving`
  // already guard the other two mutations; a double-click on Remove sends
  // a second DELETE that races the refetch and 404s on an operation that
  // actually succeeded.
  const [busyId, setBusyId] = useState<number | null>(null);

  const [names, resolveNames] = useUserNameResolver('MyClassSectionsPanel');

  // A later file selection OR a change of section must win even if an
  // earlier read resolves after it. Same superseded-read hazard the admin
  // bulk flows have (recorded in docs/FOLLOW_UPS.md) — not reproduced here,
  // because this text becomes a roster that REPLACES whatever the section
  // currently holds, so a read picked for section A landing in section B's
  // editor is a full replace of B with A's list. `resetRosterEditor` bumps
  // this for exactly that reason.
  const fileTokenRef = useRef(0);

  // Which section the textarea was last prefilled for. Keying off this
  // rather than 'is the box empty' means a switch to another section
  // always re-prefills, and a teacher who deliberately empties the box
  // never gets it silently refilled by a late `names` update.
  const prefilledForSectionRef = useRef<number | null>(null);

  // A later roster open must win even if an earlier one resolves after it.
  // Same superseded-read hazard as `fileTokenRef`, but here the loser would
  // write another section's students into an editor pointed at this one.
  const rosterTokenRef = useRef(0);
  // The mutation handlers all setState after an await; nothing else
  // suppresses those writes if the dashboard unmounts mid-request.
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const mySections = useMemo(
    () =>
      classSections
        .filter((cs) => cs.teacherId === teacherId)
        .sort((a, b) => a.periodName.localeCompare(b.periodName, undefined, { numeric: true })),
    [classSections, teacherId]
  );

  // Periods this teacher doesn't already have a section for. The backend
  // 409s on a duplicate (schoolPeriodId, teacherId); not offering the option
  // is friendlier than explaining the 409 afterwards.
  const availablePeriods = useMemo(() => {
    const taken = new Set(mySections.map((cs) => cs.schoolPeriodId));
    return schoolPeriods.filter((p) => !taken.has(p.id));
  }, [schoolPeriods, mySections]);

  useEffect(() => {
    if (!schoolId) return undefined;
    let cancelled = false;
    listSchoolPeriods(schoolId)
      .then(({ data }) => {
        if (!cancelled) setSchoolPeriods(data);
      })
      .catch((err) => {
        logError(err, 'client_api', 'MyClassSectionsPanel.listSchoolPeriods');
        if (!cancelled) setSchoolPeriods([]);
      });
    return () => {
      cancelled = true;
    };
  }, [schoolId]);

  // The conflict list and 'could we name the people in it' are one fact:
  // whenever the body the server refused becomes stale, both go. Five call
  // sites (the reset, saveRoster's success path, the textarea's onChange,
  // handleFile, Cancel) — one function, so they cannot drift.
  const clearConflicts = useCallback((): void => {
    setConflicts(null);
    setConflictNamesFailed(false);
  }, []);

  // Every piece of roster-editor state is cleared before a (re)open and on
  // close. This is the invariant that keeps section B from inheriting
  // section A's emails: PUT /roster is a full replace, so a textarea left
  // over from A would DELETE B's real roster. It was maintained by hand in
  // openRoster and closeRoster and had to be remembered twice every time a
  // new piece of editor state was added; a new piece now has exactly one
  // place to be registered. Deliberately does NOT touch `openSectionId` or
  // `rosterLoading` (each caller sets its own) or `saving` (see
  // saveRoster's `finally`).
  const resetRosterEditor = useCallback((): void => {
    // A file read still in flight was picked for the section being left;
    // its `setEmailsText` must not land in the editor being (re)opened.
    fileTokenRef.current += 1;
    prefilledForSectionRef.current = null;
    setRosterStudentIds([]);
    setEmailsText('');
    setRosterLoadFailed(false);
    setLookupFailedIds([]);
    setNamesDegradedIds([]);
    clearConflicts();
  }, [clearConflicts]);

  const openRoster = useCallback(
    async (sectionId: number): Promise<void> => {
      if (!schoolId) return;
      rosterTokenRef.current += 1;
      const token = rosterTokenRef.current;
      // Switching straight from another section's editor must not carry
      // that section's emails over: PUT /roster is a full replace, so a
      // stale textarea would delete the target section's real roster.
      resetRosterEditor();
      setOpenSectionId(sectionId);
      setRosterLoading(true);
      try {
        const { studentIds } = await getRoster(schoolId, sectionId);
        if (token !== rosterTokenRef.current) return;
        setRosterStudentIds(studentIds);
        const { failedIds } = await resolveNames(studentIds);
        if (token !== rosterTokenRef.current) return;
        setLookupFailedIds(failedIds);
      } catch (err) {
        // Logging is unconditional (see saveRoster): a read that failed is
        // worth knowing about even if the teacher has since opened another
        // section. The toast and the editor-state writes below stay owned
        // by this run — a user who has left this section neither needs the
        // toast nor should have their new editor written to.
        logError(err, 'client_api', 'MyClassSectionsPanel.getRoster');
        if (token !== rosterTokenRef.current) return;
        showError(getContextualError(err, 'teacher'));
        setRosterStudentIds([]);
        setRosterLoadFailed(true);
      } finally {
        // Only the run that still owns the editor clears the spinner; a
        // superseded run must not un-load the newer request.
        if (token === rosterTokenRef.current) setRosterLoading(false);
      }
    },
    [schoolId, resolveNames, resetRosterEditor]
  );

  // Prefill the editor with the roster's current emails once their user
  // records have resolved, so "save" without edits is a no-op rather than a
  // clear. Once per opened section — never clobber typing.
  useEffect(() => {
    if (openSectionId === null || rosterLoading) return;
    if (prefilledForSectionRef.current === openSectionId) return;
    prefilledForSectionRef.current = openSectionId;
    setEmailsText(
      rosterStudentIds
        .map((id) => names[String(id)]?.email)
        .filter((email): email is string => Boolean(email))
        .join('\n')
    );
  }, [openSectionId, rosterLoading, rosterStudentIds, names]);

  // Deliberately does NOT touch `saving` — see saveRoster's `finally`.
  const closeRoster = (): void => {
    rosterTokenRef.current += 1;
    resetRosterEditor();
    setOpenSectionId(null);
    setRosterLoading(false);
  };

  const handleCreate = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    if (!schoolId || !teacherId || !newPeriodId) return;
    setCreating(true);
    try {
      await createClassSection(schoolId, {
        schoolPeriodId: Number(newPeriodId),
        teacherId,
        name: newName.trim() || null,
      });
      if (mountedRef.current) {
        setNewPeriodId('');
        setNewName('');
      }
      await refetchClassSections();
      showSuccess('Class added.');
    } catch (err) {
      logError(err, 'client_api', 'MyClassSectionsPanel.createClassSection');
      showError(getContextualError(err, 'teacher'));
    } finally {
      if (mountedRef.current) setCreating(false);
    }
  };

  const handleRename = async (sectionId: number): Promise<void> => {
    if (!schoolId || busyId !== null) return;
    const trimmed = renameValue.trim();
    setBusyId(sectionId);
    try {
      await updateClassSection(schoolId, sectionId, { name: trimmed || null });
      if (mountedRef.current) setRenamingId(null);
      await refetchClassSections();
      showSuccess('Class renamed.');
    } catch (err) {
      logError(err, 'client_api', 'MyClassSectionsPanel.updateClassSection');
      showError(getContextualError(err, 'teacher'));
    } finally {
      if (mountedRef.current) setBusyId(null);
    }
  };

  const handleDelete = async (section: ClassSection): Promise<void> => {
    if (!schoolId || busyId !== null) return;
    const label = section.name
      ? `${section.periodName} — ${section.name}`
      : section.periodName;
    if (
      !window.confirm(
        `Remove your ${label} class? Its roster is removed too. Passes already created keep working; students just stop being filtered into this class.`
      )
    ) {
      return;
    }
    setBusyId(section.id);
    try {
      await deleteClassSection(schoolId, section.id);
      if (mountedRef.current && openSectionId === section.id) closeRoster();
      await refetchClassSections();
      showSuccess('Class removed.');
    } catch (err) {
      logError(err, 'client_api', 'MyClassSectionsPanel.deleteClassSection');
      showError(getContextualError(err, 'teacher'));
    } finally {
      if (mountedRef.current) setBusyId(null);
    }
  };

  const handleFile = async (file: File | undefined): Promise<void> => {
    if (!file) return;
    fileTokenRef.current += 1;
    const token = fileTokenRef.current;
    try {
      const text = await readFileAsText(file);
      if (token !== fileTokenRef.current) return;
      setEmailsText(text);
      // Same staleness as the textarea's onChange: a new body invalidates
      // the conflict list the server produced for the old one.
      clearConflicts();
    } catch (err) {
      if (token !== fileTokenRef.current) return;
      showError(err instanceof Error ? err.message : String(err));
    }
  };

  // Two DISJOINT failure sets: `lookupFailedIds` is written only by
  // openRoster, `namesDegradedIds` only by a successful saveRoster.
  const openFailedSet = new Set(lookupFailedIds);
  const degradedSet = new Set(namesDegradedIds);
  // The prefill maps ids through `names[...].email` and drops what it
  // can't resolve; since PUT /roster is a full replace, saving that short
  // list would drop those students. The `!names[...]?.email` pre-filter
  // means an id that failed once and resolved on a later pass falls out of
  // ALL THREE buckets, so a stale entry can never keep the gate armed.
  const unresolvedStudentIds = rosterStudentIds.filter(
    (id) => !names[String(id)]?.email
  );
  // AT RISK: the lookup threw on the OPEN path, so we do not know these
  // students' emails, and the box — whatever it now contains — cannot be
  // describing them. A full replace WOULD remove them. Note what this is
  // NOT derived from: `emailsText`. Editing the box cannot make an unknown
  // email known, so it must not be able to change this set.
  const lookupFailedStudentIds = unresolvedStudentIds.filter((id) =>
    openFailedSet.has(String(id))
  );
  // DEGRADED ONLY: the lookup threw after a save that already landed. The
  // roster equals the box, so nothing is at risk — only the labels.
  const namesDegradedStudentIds = unresolvedStudentIds.filter(
    (id) => degradedSet.has(String(id)) && !openFailedSet.has(String(id))
  );
  // GONE: the lookup succeeded and did not return them. Retrying cannot
  // help, so gating here would lock the editor forever; dropping them on
  // the next save is both true and the desired outcome — just say so.
  const missingStudentIds = unresolvedStudentIds.filter(
    (id) => !openFailedSet.has(String(id)) && !degradedSet.has(String(id))
  );
  // Hard block, unchanged in meaning: `rosterStudentIds` is [] for want of
  // data, so there is no honest consent to ask for — the teacher cannot be
  // shown WHO would be removed, which is the whole content of a consent.
  // The lookup half moves out of `disabled` and into saveRoster's consent
  // gate: there the affected students CAN be named, so the teacher can
  // make the call instead of being told to reload and lose their typing.
  const saveBlocked = rosterLoadFailed;

  // `confirmReassign` is only ever sent as the second half of a handshake the
  // server started: the user has seen the conflict list and said yes. It is
  // never sent pre-emptively, which is the whole point of the 409.
  const saveRoster = async (confirmReassign: boolean): Promise<void> => {
    if (!schoolId || openSectionId === null) return;
    // INFORMED CONSENT, at the point of the write. PUT /roster is a full
    // replace, so sending a list that cannot name these students removes
    // them. The teacher is told WHICH students, in the moment, and decides.
    // This sits inside saveRoster rather than on a button's `disabled`
    // because putRoster has exactly one call site and two callers ('Save
    // roster' and 'Move them and save'): gating here covers both, and
    // cannot be bypassed by any future affordance that reuses saveRoster.
    // Short-circuit order matters: when nothing is unnamable the confirm is
    // never reached, so no existing test path meets jsdom's unmocked
    // window.confirm (which returns undefined and would read as a decline).
    if (lookupFailedStudentIds.length > 0) {
      const who = lookupFailedStudentIds
        .map((id) => userLabel(names, id))
        .join(', ');
      const n = lookupFailedStudentIds.length;
      if (
        !window.confirm(
          `${n} student${n === 1 ? '' : 's'} on this roster could not be loaded (${who}), and unless their addresses are in the box below they WILL be removed when you save. Continue?`
        )
      ) {
        return;
      }
    }
    // Pin the target at click time: `openSectionId` can move under an
    // await, and PUT /roster is a full replace of whatever id it is given.
    const sectionId = openSectionId;
    const studentEmails = parseEmailList(emailsText);
    // Captured, never bumped: a save must not invalidate itself, and it
    // must not invalidate a concurrent open either.
    const token = rosterTokenRef.current;
    setSaving(true);
    try {
      const result = await putRoster(schoolId, sectionId, {
        studentEmails,
        ...(confirmReassign ? { confirmReassign: true } : {}),
      });
      const moved = result.reassigned?.length ?? 0;
      // The write landed either way, so the toast is unconditional.
      showSuccess(
        moved > 0
          ? `Roster saved. ${moved} student${moved === 1 ? '' : 's'} moved out of another class.`
          : 'Roster saved.'
      );
      // Resolve BEFORE publishing the new ids: the "unresolved students"
      // block below is derived from `rosterStudentIds` vs `names`, and
      // writing the ids first would flash that warning (and disable Save)
      // for the whole duration of the listUsers round trip.
      const { failedIds } = await resolveNames(result.studentIds);
      if (token !== rosterTokenRef.current) return;
      clearConflicts();
      // The write LANDED: the section's roster is now exactly the list the
      // box described, so whatever the open path could not name is either
      // gone from the roster or still spelled out in the box. Nothing can
      // be truncated by re-sending it, so the gate signal is retired...
      setLookupFailedIds([]);
      // ...but the failure is not swallowed: it is why the summary above
      // renders "Student #<id>", and saying nothing would leave that
      // looking like the name those students actually have.
      setNamesDegradedIds(failedIds);
      setRosterStudentIds(result.studentIds);
    } catch (err) {
      const found = readConflicts(err);
      const stale = token !== rosterTokenRef.current;
      // Failure telemetry and the toast are unconditional for anything we
      // cannot hand to the user as the 409 handshake: the PUT did not land,
      // and a teacher who closed the editor or switched sections must still
      // be told, and we must still log it. Only the editor-state writes
      // below are gated on ownership.
      if (!found || stale) {
        logError(err, 'client_api', 'MyClassSectionsPanel.putRoster');
        showError(getContextualError(err, 'teacher'));
      }
      if (stale) return;
      if (found) {
        setConflicts(found);
        const { failedIds } = await resolveNames(found.map((c) => c.studentId));
        // Second await, second ownership check: the editor can move under
        // it, and this write is editor state.
        if (token !== rosterTokenRef.current) return;
        setConflictNamesFailed(failedIds.length > 0);
      }
    } finally {
      // NOT gated on the roster token. `saving` is a component-global flag
      // for a single in-flight PUT, and openRoster/closeRoster both bump
      // that token while nothing disables 'Close roster' or another row's
      // 'Edit roster' during a save — gating here would leave `saving`
      // stuck true forever. Only an unmount can skip the cleanup.
      if (mountedRef.current) setSaving(false);
    }
  };

  // The route this panel drives is teacher-scoped; there is nothing here for
  // a caller with no school (a SUPER_ADMIN acting globally).
  if (!schoolId || !teacherId) return null;

  const openSection = mySections.find((cs) => cs.id === openSectionId) ?? null;

  // Re-runs the whole open for the section currently in the editor. This is
  // the ONLY repair affordance: repairing `names` alone would leave the
  // textarea holding the short list the first prefill produced while
  // re-enabling Save — a silent full-replace roster truncation. Going
  // through openRoster resets `prefilledForSectionRef`, so the repaired
  // names actually reach the box.
  const reloadOpenRoster = (): void => {
    if (openSectionId === null) return;
    // Reload re-runs the open, which clears the box. Confirm rather than
    // disable: on the rosterLoadFailed branch Save is disabled, so a
    // disabled Reload would be a true dead end. The test is "is there
    // anything to lose", NOT "does this match the prefill" — no
    // prefill-identity comparison exists anywhere in this component, by
    // design (see the consent gate in saveRoster). Over-confirming on an
    // untouched non-empty prefill is the safe direction to be wrong in.
    if (
      emailsText.trim() !== '' &&
      !window.confirm(
        'Reloading replaces the box with the roster on file. Discard what is in it?'
      )
    ) {
      return;
    }
    void openRoster(openSectionId);
  };

  // Hoisted so the two blocking branches provably share one behaviour and
  // one label, and cannot diverge again.
  const reloadButton = (
    <button
      type="button"
      className={adminStyles['btn-edit']}
      onClick={reloadOpenRoster}
    >
      Reload
    </button>
  );

  return (
    <CollapsibleSection
      title="My Classes"
      icon="📋"
      id="teacher-class-sections"
      defaultExpanded={false}
    >
      <div className={styles['panel']}>
        <p className={styles['intro']}>
          Optional. Add your classes and their students to filter the pass
          board by class. Passes work without this — students who aren&apos;t
          on any roster still show up on the board.
        </p>

        {loading ? (
          <p className={styles['empty']}>Loading your classes…</p>
        ) : mySections.length === 0 ? (
          <p className={styles['empty']}>You have no classes yet.</p>
        ) : (
          <ul className={styles['section-list']}>
            {mySections.map((section) => (
              <li key={section.id} className={styles['section-row']}>
                <div className={styles['section-label']}>
                  <strong>{section.periodName}</strong>
                  {renamingId === section.id ? (
                    <>
                      <input
                        type="text"
                        value={renameValue}
                        aria-label={`Name for ${section.periodName}`}
                        onChange={(e) => setRenameValue(e.target.value)}
                        className={styles['rename-input']}
                      />
                      {/* `disabled` matches handleRename's own
                          `busyId !== null` guard and the sibling
                          Rename/Remove buttons: otherwise a mutation on
                          ANOTHER row leaves this enabled and the click is
                          silently swallowed. The per-row predicate is only
                          for the label swap. */}
                      <button
                        type="button"
                        className={adminStyles['add-btn']}
                        disabled={busyId !== null}
                        onClick={() => handleRename(section.id)}
                      >
                        {busyId === section.id ? 'Saving…' : 'Save name'}
                      </button>
                      <button
                        type="button"
                        className={adminStyles['btn-edit']}
                        onClick={() => setRenamingId(null)}
                      >
                        Cancel
                      </button>
                    </>
                  ) : (
                    <>
                      <span className={styles['section-name']}>
                        {section.name || 'Unnamed class'}
                      </span>
                      <button
                        type="button"
                        className={adminStyles['btn-edit']}
                        disabled={busyId !== null}
                        aria-label={`Rename ${section.periodName}`}
                        onClick={() => {
                          setRenamingId(section.id);
                          setRenameValue(section.name ?? '');
                        }}
                      >
                        Rename
                      </button>
                    </>
                  )}
                </div>
                <div className={styles['section-actions']}>
                  <button
                    type="button"
                    className={adminStyles['btn-edit']}
                    aria-label={
                      openSectionId === section.id
                        ? `Close roster for ${section.periodName}`
                        : `Edit roster for ${section.periodName}`
                    }
                    onClick={() =>
                      openSectionId === section.id
                        ? closeRoster()
                        : openRoster(section.id)
                    }
                  >
                    {openSectionId === section.id ? 'Close roster' : 'Edit roster'}
                  </button>
                  <button
                    type="button"
                    className={adminStyles['remove-btn']}
                    disabled={busyId !== null}
                    aria-label={`Remove ${section.periodName}`}
                    onClick={() => handleDelete(section)}
                  >
                    {busyId === section.id ? 'Removing…' : 'Remove'}
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}

        {openSection && (
          <div className={styles['roster-editor']}>
            <h3>
              Roster — {openSection.periodName}
              {openSection.name ? ` (${openSection.name})` : ''}
            </h3>

            {rosterLoading ? (
              <p>Loading roster…</p>
            ) : (
              <>
                <p className={styles['hint']}>
                  {rosterStudentIds.length === 0
                    ? 'No students yet.'
                    : `${rosterStudentIds.length} student${rosterStudentIds.length === 1 ? '' : 's'}: ${rosterStudentIds.map((id) => userLabel(names, id)).join(', ')}`}
                </p>
                <label htmlFor="roster-emails">
                  Student emails — one per line, or pasted from a spreadsheet
                  column. Saving replaces the whole roster.
                </label>
                <textarea
                  id="roster-emails"
                  value={emailsText}
                  rows={8}
                  onChange={(e) => {
                    setEmailsText(e.target.value);
                    // The conflict list describes the body the server
                    // refused; once the body changes it is stale, and
                    // 'Move them and save' would confirm against it.
                    if (conflicts) clearConflicts();
                  }}
                  className={styles['emails-input']}
                />
                {rosterLoadFailed ? (
                  <p className={styles['warning']} role="status">
                    Could not load this roster. Saving now would replace it
                    with an empty list — reload before saving.{' '}
                    {reloadButton}
                  </p>
                ) : lookupFailedStudentIds.length > 0 ? (
                  <p className={styles['warning']} role="status">
                    Could not load {lookupFailedStudentIds.length} student
                    {lookupFailedStudentIds.length === 1 ? '' : 's'} on this
                    roster — they show as &ldquo;Student #id&rdquo; above.
                    Saving replaces the roster with the list in the box, which
                    would remove them; reload to try again, or confirm when you
                    save. {reloadButton}
                  </p>
                ) : namesDegradedStudentIds.length > 0 ? (
                  // The save landed; only the labels above are degraded.
                  // Non-blocking, and deliberately no Reload button — Save is
                  // enabled here, and a one-click reload could discard a
                  // roster the teacher just pasted (same reason as below).
                  <p className={styles['hint']} role="status">
                    Could not load {namesDegradedStudentIds.length} student name
                    {namesDegradedStudentIds.length === 1 ? '' : 's'} on this
                    roster; the roster itself saved correctly.
                  </p>
                ) : missingStudentIds.length > 0 ? (
                  // Non-blocking, and deliberately WITHOUT a Reload button:
                  // Save is enabled here, so a one-click reload would let a
                  // teacher discard a roster they had just pasted. Close
                  // roster + Edit roster already reloads.
                  <p className={styles['hint']} role="status">
                    {missingStudentIds.length} student
                    {missingStudentIds.length === 1 ? '' : 's'} on this roster
                    no longer
                    {missingStudentIds.length === 1
                      ? ' has an account'
                      : ' have accounts'}{' '}
                    at this school; saving will remove them from it.
                  </p>
                ) : null}
                <div className={styles['roster-actions']}>
                  <input
                    id="roster-emails-file"
                    type="file"
                    accept=".csv,.txt"
                    aria-label="Upload a list of student emails"
                    className={adminStyles['file-input']}
                    onChange={(e) => handleFile(e.target.files?.[0])}
                  />
                  <button
                    type="button"
                    className={adminStyles['add-btn']}
                    disabled={saving || saveBlocked}
                    onClick={() => saveRoster(false)}
                  >
                    {saving ? 'Saving…' : 'Save roster'}
                  </button>
                </div>
              </>
            )}

            {conflicts && (
              <div className={styles['conflicts']} role="alert">
                <p>
                  {conflicts.length} student
                  {conflicts.length === 1 ? ' is' : 's are'} already in another
                  teacher&apos;s class this period. Saving moves them into your
                  class and out of that one.
                </p>
                <ul>
                  {conflicts.map((c) => (
                    <li key={c.studentId}>
                      {userLabel(names, c.studentId)} — currently in{' '}
                      {c.fromTeacherName ?? 'another teacher'}
                      {c.fromSectionName ? `'s ${c.fromSectionName}` : "'s class"}
                    </li>
                  ))}
                </ul>
                {conflictNamesFailed && (
                  <p className={styles['warning']}>
                    Could not load these students&apos; names — the rows above
                    are ids, not the names on their accounts. Reload before
                    confirming.
                  </p>
                )}
                <button
                  type="button"
                  className={adminStyles['add-btn']}
                  disabled={saving || saveBlocked || conflictNamesFailed}
                  onClick={() => saveRoster(true)}
                >
                  Move them and save
                </button>
                <button
                  type="button"
                  className={adminStyles['btn-edit']}
                  onClick={() => clearConflicts()}
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        )}

        {!loading && (
          <form className={styles['create-form']} onSubmit={handleCreate}>
            <h3>Add a class</h3>
            {availablePeriods.length === 0 ? (
              <p className={styles['hint']}>
                You already have a class for every period.
              </p>
            ) : (
              <>
                <label htmlFor="new-section-period">Period</label>
                <select
                  id="new-section-period"
                  value={newPeriodId}
                  onChange={(e) => setNewPeriodId(e.target.value)}
                >
                  <option value="">Select a period…</option>
                  {availablePeriods.map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
                <label htmlFor="new-section-name">Name (optional)</label>
                <input
                  id="new-section-name"
                  type="text"
                  value={newName}
                  placeholder="Algebra I"
                  onChange={(e) => setNewName(e.target.value)}
                />
                <button
                  type="submit"
                  className={adminStyles['add-btn']}
                  disabled={creating || !newPeriodId}
                >
                  {creating ? 'Adding…' : 'Add class'}
                </button>
              </>
            )}
          </form>
        )}
      </div>
    </CollapsibleSection>
  );
};

export default MyClassSectionsPanel;
